import React, { useEffect, useState, useMemo } from 'react';
import { Loader2, Search as SearchIcon, History as HistoryIcon, CheckCircle2, Clock } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const LABELS = { vpower: 'V-Power', super: 'Súper', regular: 'Regular', diesel: 'Diesel', ulsd: 'ULSD' };
const ORDER = ['vpower', 'super', 'regular', 'diesel', 'ulsd'];
const ORIGINS = { masivo: 'Push masivo', tracking: 'Tracking', monitor: 'Monitor', rollback: 'Rollback', oficial: 'Oficial' };
const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const cell = (p) => p == null ? '—' : `Q${Number(p).toFixed(2)}`;

const Sel = ({ value, onChange, options, all, testid }) => (
  <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
    className="h-9 px-2 rounded-md border border-[#e8e5de] bg-[#fafaf6] text-[12px]">
    <option value="">{all}</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

export const CurrentPrices = () => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [fBrand, setFBrand] = useState('');
  const [fBu, setFBu] = useState('');
  const [fDep, setFDep] = useState('');
  const [fMun, setFMun] = useState('');
  const [fTA, setFTA] = useState('');
  const [movStation, setMovStation] = useState(null);
  const [movs, setMovs] = useState(null);

  useEffect(() => {
    api.get('/price-pushes/current-prices').then(r => setRows(r.data))
      .catch(() => toast.error('Error cargando precios vigentes'));
  }, []);

  const opts = useMemo(() => ({
    brands: [...new Set((rows || []).map(r => r.brand).filter(Boolean))].sort(),
    bus: [...new Set((rows || []).map(r => r.bu).filter(Boolean))].sort(),
    deps: [...new Set((rows || []).map(r => r.departamento))].sort(),
    muns: [...new Set((rows || []).filter(r => !fDep || r.departamento === fDep).map(r => r.municipio))].sort(),
    tas: [...new Set((rows || []).map(r => r.trade_area).filter(Boolean))].sort(),
  }), [rows, fDep]);

  const filtered = useMemo(() => (rows || []).filter(r =>
    (!q || r.station_name.toLowerCase().includes(q.toLowerCase())) &&
    (!fBrand || r.brand === fBrand) && (!fBu || r.bu === fBu) &&
    (!fDep || r.departamento === fDep) && (!fMun || r.municipio === fMun) && (!fTA || r.trade_area === fTA)
  ), [rows, q, fBrand, fBu, fDep, fMun, fTA]);

  const grouped = useMemo(() => {
    const deps = {};
    for (const r of filtered) {
      const d = deps[r.departamento] = deps[r.departamento] || {};
      (d[r.municipio] = d[r.municipio] || []).push(r);
    }
    return Object.entries(deps).sort(([a], [b]) => a.localeCompare(b))
      .map(([dep, muns]) => [dep, Object.entries(muns).sort(([a], [b]) => a.localeCompare(b))]);
  }, [filtered]);

  const openMovs = (r) => {
    setMovStation(r); setMovs(null);
    api.get(`/stations/${r.station_id}/price-movements`).then(x => setMovs(x.data)).catch(() => setMovs([]));
  };

  if (rows === null) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>;

  return (
    <div className="space-y-4" data-testid="current-prices-panel">
      <div className="bg-white border border-[#e8e5de] rounded-xl p-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8a8579]"/>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar sitio…" className="h-9 pl-8 w-[190px] bg-[#fafaf6]" data-testid="vp-search"/>
        </div>
        <Sel value={fBrand} onChange={setFBrand} options={opts.brands} all="Todas las marcas" testid="vp-brand"/>
        <Sel value={fBu} onChange={setFBu} options={opts.bus} all="Todos los BU" testid="vp-bu"/>
        <Sel value={fDep} onChange={v => { setFDep(v); setFMun(''); }} options={opts.deps} all="Todos los departamentos" testid="vp-dep"/>
        <Sel value={fMun} onChange={setFMun} options={opts.muns} all="Todos los municipios" testid="vp-mun"/>
        <Sel value={fTA} onChange={setFTA} options={opts.tas} all="Todas las Trade Áreas" testid="vp-ta"/>
        <span className="text-[11.5px] text-[#8a8579] ml-auto">{filtered.length} sitio(s)</span>
      </div>

      {grouped.map(([dep, muns]) => (
        <div key={dep} className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
          <p className="px-4 py-2.5 bg-[#1c1c1c] text-white text-[12px] uppercase tracking-wider font-medium">{dep}</p>
          {muns.map(([mun, sts]) => (
            <div key={mun}>
              <p className="px-4 py-2 bg-[#fafaf6] border-y border-[#e8e5de] text-[11.5px] font-medium text-[#5a5a5a]">{mun} · {sts.length} sitio(s)</p>
              <table className="w-full text-[11.5px]">
                <thead>
                  <tr className="text-left text-[9.5px] uppercase tracking-wider text-[#8a8579]">
                    <th className="py-1.5 px-4 font-normal">Sitio</th>
                    {ORDER.map(f => <th key={f} className="py-1.5 px-2 font-normal text-right">{LABELS[f]}<br/><span className="normal-case">Self / Full</span></th>)}
                    <th className="py-1.5 px-2 font-normal">Vigente desde</th>
                    <th className="py-1.5 px-2 font-normal">Origen</th>
                    <th className="py-1.5 px-2 font-normal">Movs.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f0ede6]">
                  {sts.map(r => (
                    <tr key={r.station_id} className="hover:bg-[#fafaf6] cursor-pointer" onClick={() => openMovs(r)} data-testid={`vp-row-${r.station_id}`}>
                      <td className="py-2 px-4">
                        <span className="font-medium">{r.station_name}</span>
                        <span className="text-[10px] text-[#8a8579]"> {r.brand}{r.bu ? ` · ${r.bu}` : ''}</span>
                        {r.trade_area && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-[#e7eeff] text-[#0f1d6f]">TA: {r.trade_area}</span>}
                        {!r.confirmed && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-[#fef3c7] text-[#92400e]">sin foto</span>}
                      </td>
                      {ORDER.map(f => {
                        const p = r.prices?.[f];
                        return <td key={f} className="py-2 px-2 text-right font-mono tabular-nums text-[10.5px] whitespace-nowrap">{p ? `${cell(p.self)} / ${cell(p.full)}` : '—'}</td>;
                      })}
                      <td className="py-2 px-2 whitespace-nowrap">{fmt(r.since)}</td>
                      <td className="py-2 px-2">{ORIGINS[r.origin] || r.origin}{r.authorized_by ? <span className="text-[10px] text-[#8a8579]"> · {r.authorized_by}</span> : null}</td>
                      <td className="py-2 px-2"><span className="inline-flex items-center gap-1 text-[#0f1d6f]"><HistoryIcon size={11}/>{r.movements}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      ))}
      {grouped.length === 0 && <p className="text-[13px] text-[#8a8579] py-6 text-center">Sin sitios con los filtros seleccionados.</p>}

      <Dialog open={!!movStation} onOpenChange={(o) => { if (!o) setMovStation(null); }}>
        <DialogContent className="max-w-2xl bg-[#f8f6f1] max-h-[80vh] overflow-y-auto" data-testid="movements-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[20px]">Movimientos de Pricing · {movStation?.station_name}</DialogTitle>
          </DialogHeader>
          {movs === null ? <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
            : movs.length === 0 ? <p className="text-[12.5px] text-[#8a8579]">Sin movimientos registrados.</p>
            : (
            <div className="space-y-2">
              {movs.map(m => (
                <div key={m.id} className="bg-white border border-[#e8e5de] rounded-lg p-3" data-testid={`movement-${m.id}`}>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px]">
                    <span className="font-medium">{fmt(m.effective_at)}</span>
                    <span className="px-2 py-0.5 rounded-full bg-[#e0e7ff] text-[#3730a3] text-[10px] font-medium">{ORIGINS[m.origin] || m.origin}</span>
                    {m.created_by_name && <span className="text-[#5a5a5a]">por {m.created_by_name}</span>}
                    <span className="ml-auto">
                      {m.status === 'cancelled' ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#e5e7eb] text-[#4b5563]">Cancelado</span>
                        : m.confirmed_at ? <span className="inline-flex items-center gap-1 text-[10px] text-[#166534]"><CheckCircle2 size={11}/> Confirmado {fmt(m.confirmed_at)}</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] text-[#92400e]"><Clock size={11}/> Sin foto</span>}
                    </span>
                  </div>
                  <p className="mt-1.5 font-mono tabular-nums text-[10.5px] text-[#3a3a3a]">
                    {ORDER.filter(f => m.final_prices?.[f]).map(f => `${LABELS[f]}: ${cell(m.final_prices[f].self)}${m.final_prices[f].full != null ? ` / ${cell(m.final_prices[f].full)}` : ''}`).join(' · ')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
